import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, IconModerator } from '../../../base/icons';
import { setWebinarHidden } from '../../../base/participants/actions';
import { getLocalParticipant, isLocalParticipantModerator } from '../../../base/participants/functions';
import { Switch } from '../../../base/react';
import { setMediaBlockAllPolicy } from '../../../webinar/actions';
import { amIInBackstage, getMainRoomPolicy, isHiddenMeEnabled, isMediaBlockAllEnabled } from '../../../webinar/functions';
import { useModerationStyles } from './styles';
/**
 * The room-wide moderator switches of the moderation pane:
 *
 * - AV moderation, the entry point for the ask-to-unmute flow (the stock
 *   control lives in FooterContextMenu, which is never rendered in this fork).
 * - Hiding the moderator's own tile from the audience (webinar-hidden IQ).
 *
 * @returns {JSX.Element | null}
 */
const RoomControlsSection = () => {
    const { t } = useTranslation();
    const { classes, cx } = useModerationStyles();
    const dispatch = useDispatch();
    const isModerator = useSelector(isLocalParticipantModerator);
    const localParticipant = useSelector(getLocalParticipant);
    // The media block-all switch is room metadata of the main room. Backstage
    // is its own room, so from there it reads the main room's policy as
    // pushed by Prosody and writes through Prosody: the metadata component
    // only accepts a write from an occupant of the room, which a backstage
    // moderator is not.
    const inBackstage = useSelector(amIInBackstage);
    const mainRoomPolicy = useSelector(getMainRoomPolicy);
    const mediaBlockAllHere = useSelector(isMediaBlockAllEnabled);
    const mediaBlockAll = inBackstage
        ? Boolean(mainRoomPolicy === null || mainRoomPolicy === void 0 ? void 0 : mainRoomPolicy.mediaBlockAll)
        : mediaBlockAllHere;
    const hidden = Boolean(localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.webinarHidden);
    // The hide-me switch is a meeting feature (ENABLE_HIDDEN_ME in the token).
    // Still shown to somebody who is hidden right now with the feature off,
    // or they would have no way to show themselves again.
    const showHideSelf = useSelector(isHiddenMeEnabled) || hidden;
    const onToggleMediaBlockAll = useCallback((value) => {
        dispatch(setMediaBlockAllPolicy(value));
    }, [dispatch]);
    const onToggleHideSelf = useCallback((value) => {
        (localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id) && dispatch(setWebinarHidden(localParticipant.id, value));
    }, [dispatch, localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id]);
    if (!isModerator) {
        return null;
    }
    return (React.createElement("div", { className: classes.card },
        React.createElement("div", { className: classes.cardHeader },
            React.createElement("div", { className: classes.cardIcon },
                React.createElement(Icon, { size: 16, src: IconModerator })),
            React.createElement("span", { className: classes.cardTitle }, t('participantsPane.headings.roomControls'))),
        React.createElement("div", { className: cx(classes.row, classes.rowDivided) },
            React.createElement("div", { className: classes.rowText },
                React.createElement("label", { className: classes.rowLabel, htmlFor: 'media-block-all-switch' }, t('participantsPane.actions.blockEveryoneMicCamera')),
                React.createElement("span", { className: classes.rowHint }, t('participantsPane.actions.blockEveryoneMicCameraHint'))),
            React.createElement(Switch, { id: 'media-block-all-switch', onValueChange: onToggleMediaBlockAll, value: mediaBlockAll })),
        showHideSelf && (React.createElement("div", { className: cx(classes.row, classes.rowDivided) },
            React.createElement("div", { className: classes.rowText },
                React.createElement("label", { className: classes.rowLabel, htmlFor: 'hide-self-switch' }, t('webinar.hideSelfLabel')),
                React.createElement("span", { className: classes.rowHint }, t('webinar.hideSelfHint'))),
            React.createElement(Switch, { id: 'hide-self-switch', onValueChange: onToggleHideSelf, value: hidden })))));
};
export default RoomControlsSection;
